import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TransitionGroup } from 'react-transition-group';
import { Fade, makeStyles } from '@material-ui/core';
import ActionButton from '../../actionButton';
import VirtualizedList from '../../virtualizedList';
import Tag from '../../tag';
import AttributeChip from '../../attributeChip';
import ReorderItems from '../../reorderItems';
import TextBoxWithTooltip from '../../textBoxWithTooltip';
import VirtualUser from '../virtualUser';
import { reorder } from '../../../../utility/helpers';
import { useElementMaxHeight } from '../../../../utility/hooks';
import { ACTION_BUTTON_TYPES } from '../../actionButton/config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: spacing(2),
    width: '100%',
  },
  rootVertical: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
  attributeItemWrapper: {
    display: 'flex',
    minWidth: 0,
  },
  tagWrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    width: 'fit-content',
    maxWidth: '100%',
  },
  itemWrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    maxWidth: '100%',
    marginBottom: spacing(2),
    alignItems: 'center',
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  selectedUserWrapper: {
    borderBottom: `1px solid ${primary.bluish7}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  selectedUsersViewport: {
    maxHeight: ({ shouldCalculateSelectionHeight, maxHeight }) =>
      shouldCalculateSelectionHeight ? maxHeight : 348,
  },
  selectedUsersWrapper: {
    marginTop: spacing(6),
    flex: 1,
  },
  selectedUsersScrollY: {
    right: -19,
  },
  button: {
    flexShrink: 0,
    width: 24,
    height: 24,
    marginLeft: spacing(1),
  },
  icon: {
    width: 16,
    height: 16,
  },
}));

const SelectedItems = ({
  selectedUsersViewportClass = undefined,
  parentNode = null,
  items,
  isTag = false,
  isAttribute = false,
  isUser = false,
  isAttributeDraggable = false,
  hasAttributeComments = false,
  hasAttributePreview = false,
  shouldCalculateSelectionHeight = false,
  onReorder = () => {},
  onDeselectItem,
  onToggleAttributeComments = () => {},
  onAttributePreview = () => {},
}) => {
  const [setElementRef, maxHeight] = useElementMaxHeight(
    parentNode,
    shouldCalculateSelectionHeight
  );
  const classes = useStyles({ shouldCalculateSelectionHeight, maxHeight });

  const getVirtualUserSize = useCallback(
    index => (index === items.length - 1 ? 24 : 32),
    [items.length]
  );

  const handleReorderAttributes = useCallback(
    result => {
      if (
        !result.destination ||
        result.source.index === result.destination.index
      )
        return;

      const updatedOrder = reorder(
        items,
        result.source.index,
        result.destination.index
      );

      return onReorder(updatedOrder);
    },
    [onReorder, items]
  );

  const handleRemoveItem = useCallback(
    item => () => {
      onDeselectItem(item);
    },
    [onDeselectItem]
  );

  const handleToggleAttributeComments = useCallback(
    attribute => value => {
      onToggleAttributeComments(attribute, value);
    },
    [onToggleAttributeComments]
  );

  const renderSelectedUser = useCallback(
    user => {
      return <VirtualUser user={user} onRemove={onDeselectItem} />;
    },
    [onDeselectItem]
  );

  if (isUser) {
    return (
      <div ref={setElementRef} className={classes.selectedUsersWrapper}>
        <VirtualizedList
          viewportClass={classNames(
            classes.selectedUsersViewport,
            selectedUsersViewportClass
          )}
          verticalScrollClass={classes.selectedUsersScrollY}
          itemClass={classes.selectedUserWrapper}
          items={items}
          count={items?.length ?? 0}
          gap={8}
          estimateSize={getVirtualUserSize}
          renderItem={renderSelectedUser}
        />
      </div>
    );
  }

  if (isAttribute) {
    return (
      <ReorderItems
        className={classNames(classes.root, classes.rootVertical)}
        itemClass={classes.attributeItemWrapper}
        isDragDisabled={!isAttributeDraggable}
        onSave={handleReorderAttributes}
      >
        {items.map(item => (
          <AttributeChip
            key={`attribute_${item.id}`}
            id={item.id}
            attribute={item}
            name={item.name}
            color={item?.color}
            isAttributeDraggable={isAttributeDraggable}
            hasCommentsSwitch={hasAttributeComments}
            hasPreview={hasAttributePreview}
            onRemove={onDeselectItem}
            onToggleComments={handleToggleAttributeComments(item)}
            onPreview={onAttributePreview}
            isSelected
            hasRemove
          />
        ))}
      </ReorderItems>
    );
  }

  if (isTag) {
    return (
      <TransitionGroup className={classes.root}>
        {items.map(item => (
          <Fade key={`value${item.id}`} in>
            <div className={classes.tagWrapper}>
              <Tag
                tag={item}
                color={item?.color}
                onTagSelect={onDeselectItem}
                isSelectable
                isSelected
                hasRemove
              />
            </div>
          </Fade>
        ))}
      </TransitionGroup>
    );
  }

  return (
    <TransitionGroup className={classNames(classes.root, classes.rootVertical)}>
      {items.map(item => (
        <Fade key={`value${item.id}`} in>
          <div className={classes.itemWrapper}>
            <TextBoxWithTooltip
              variant="subtitle2"
              text={item.title || item.name}
            />
            <ActionButton
              className={classes.button}
              iconClass={classes.icon}
              type={ACTION_BUTTON_TYPES.CLOSE}
              size="small"
              onClickHandler={handleRemoveItem(item)}
            />
          </div>
        </Fade>
      ))}
    </TransitionGroup>
  );
};

SelectedItems.propTypes = {
  selectedUsersViewportClass: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  isUser: PropTypes.bool,
  isTag: PropTypes.bool,
  isAttribute: PropTypes.bool,
  hasAttributePreview: PropTypes.bool,
  isAttributeDraggable: PropTypes.bool,
  hasAttributeComments: PropTypes.bool,
  shouldCalculateSelectionHeight: PropTypes.bool,
  parentNode: PropTypes.object,
  onDeselectItem: PropTypes.func.isRequired,
  onReorder: PropTypes.func,
  onToggleAttributeComments: PropTypes.func,
  onAttributePreview: PropTypes.func,
};

export default SelectedItems;
